import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const ProfileImageUploader = ({ onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(null);
  // ローカルプレビュー用
  const [pendingFile, setPendingFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const { t } = useTranslation('profile');

  const fetchCurrent = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/user`);
      setCurrentUrl(res.data.profile_image_url || null);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    fetchCurrent();
  }, []);

  // ObjectURL のメモリ解放（アンマウント時のみ）
  const previewUrlRef = useRef(null);
  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  // ファイル選択時: プレビューのみ表示（まだアップロードしない）
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // 前の ObjectURL を解放
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    setPendingFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 確定ボタン: 実際にアップロード
  const handleConfirm = async () => {
    if (!pendingFile) return;
    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('image', pendingFile);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/profile-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data && res.data.url) {
        setCurrentUrl(res.data.url);
        if (onUploaded) onUploaded(res.data);
      }
    } catch (err) {
      setError(t('imageUploader.uploadFailed'));
    } finally {
      setUploading(false);
      // previewUrl はそのまま残す（CDNキャッシュより確実に新しい画像を表示するため）
      // 次のファイル選択時 or アンマウント時に解放される
      setPendingFile(null);
    }
  };

  // キャンセル: プレビューを破棄
  const handleCancel = () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    setPendingFile(null);
    setPreviewUrl(null);
    setError('');
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/user/profile-image`);
      setCurrentUrl(null);
      if (onUploaded) onUploaded(null);
      setShowDeleteModal(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(t('imageUploader.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const displayUrl = previewUrl || currentUrl || '/default-profile.png';

  return (
    <div style={{ marginBottom: 12 }}>
      {error && <p style={{ color: '#dc2626', margin: '0 0 8px' }}>{error}</p>}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* 画像プレビュー */}
        <div style={{ position: 'relative', width: 96, height: 96, borderRadius: 8, overflow: 'hidden', background: '#f4f4f4', flexShrink: 0 }}>
          <img src={displayUrl} alt={t('profileImageAlt')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          {pendingFile && (
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.45)', color: '#fff', fontSize: 10, textAlign: 'center', padding: '2px 0' }}>
              {t('imageUploader.preview')}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            disabled={uploading || deleting}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />

          {/* 未確定ファイルあり: 保存 / キャンセル */}
          {pendingFile ? (
            <>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={uploading}
                style={{ border: '1px solid #2563eb', background: uploading ? '#dbeafe' : '#2563eb', color: '#fff', borderRadius: 8, padding: '8px 12px', fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer', fontSize: 14 }}
              >
                {uploading ? t('imageUploader.uploading') : t('imageUploader.saveBtn')}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={uploading}
                style={{ border: '1px solid #bbb', background: '#fff', color: '#555', borderRadius: 8, padding: '8px 12px', fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer', fontSize: 14 }}
              >
                {t('imageUploader.cancelBtn')}
              </button>
            </>
          ) : (
            /* 通常状態: 画像を変更 / 削除 */
            <>
              <button
                type="button"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={uploading || deleting}
                style={{ border: '1px solid #bbb', background: '#fff', color: '#333', borderRadius: 8, padding: '8px 12px', fontWeight: 600, cursor: uploading || deleting ? 'not-allowed' : 'pointer', fontSize: 14 }}
              >
                {t('imageUploader.changeBtn')}
              </button>
              {currentUrl && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  style={{ border: '1px solid #dc2626', background: '#fff', color: '#dc2626', borderRadius: 8, padding: '8px 12px', fontWeight: 600, cursor: deleting ? 'not-allowed' : 'pointer', fontSize: 14 }}
                >
                  {t('imageUploader.deleteIconBtn')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {showDeleteModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 2000,
          }}
          onClick={() => {
            if (!deleting) setShowDeleteModal(false);
          }}
        >
          <div
            style={{
              width: 'min(420px, 100%)',
              background: '#fff',
              border: '1px solid #ddd',
              borderRadius: 10,
              padding: 16,
              boxShadow: '0 16px 32px rgba(0,0,0,.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{t('imageUploader.deleteTitle')}</h3>
            <p style={{ margin: '10px 0 0', color: '#444', fontSize: 14 }}>{t('imageUploader.deleteConfirm')}</p>
            <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                style={{ border: '1px solid #dc2626', background: '#fff', color: '#dc2626', borderRadius: 8, padding: '8px 12px', fontWeight: 700, cursor: 'pointer' }}
              >
                {deleting ? t('imageUploader.deleting') : t('imageUploader.deleteBtn')}
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                style={{ border: '1px solid #bbb', background: '#fff', color: '#333', borderRadius: 8, padding: '8px 12px', fontWeight: 700, cursor: 'pointer' }}
              >
                {t('imageUploader.back')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileImageUploader;
